import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { ArrowLeft, Plus, Trash2, Users, Building2, KeyRound } from "lucide-react";

const emptyUser = { email: "", password: "", name: "", role: "member" };

const ROLE_LABELS = {
    super_admin: "Super-admin",
    tenant_admin: "Beheerder",
    member: "Medewerker",
};

export default function TenantDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loadError, setLoadError] = useState("");
    const [form, setForm] = useState(emptyUser);
    const [showForm, setShowForm] = useState(false);
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoadError("");
        try {
            const { data } = await api.get(`/admin/tenants/${id}`);
            setData(data);
        } catch (err) {
            setLoadError(
                formatApiErrorDetail(err.response?.data?.detail) || err.message
            );
        }
    };
    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const save = async (e) => {
        e.preventDefault();
        setError("");
        setSaving(true);
        try {
            await api.post(`/admin/tenants/${id}/users`, form);
            setForm(emptyUser);
            setShowForm(false);
            await load();
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSaving(false);
        }
    };

    const removeUser = async (u) => {
        if (!window.confirm(`Gebruiker ${u.email} verwijderen uit ${data.tenant.name}?`)) return;
        try {
            await api.delete(`/admin/tenants/${id}/users/${u.id}`);
            await load();
        } catch (err) {
            alert(formatApiErrorDetail(err.response?.data?.detail) || "Verwijderen mislukt");
        }
    };

    const [resetting, setResetting] = useState(null); // {user, pwd, error}
    const submitReset = async () => {
        if (!resetting) return;
        if (!resetting.pwd || resetting.pwd.length < 8) {
            setResetting({ ...resetting, error: "Minimaal 8 tekens" });
            return;
        }
        try {
            await api.post(`/admin/users/${resetting.user.id}/reset-password`, {
                new_password: resetting.pwd,
            });
            setResetting({ ...resetting, pwd: "", error: "", done: true });
        } catch (err) {
            setResetting({
                ...resetting,
                error: formatApiErrorDetail(err.response?.data?.detail) || "Resetten mislukt",
            });
        }
    };

    const removeTenant = async () => {
        if (!data?.tenant) return;
        if (!window.confirm(`Tenant "${data.tenant.name}" VOLLEDIG verwijderen incl. alle data?`))
            return;
        try {
            await api.delete(`/admin/tenants/${id}`);
            navigate("/instellingen", { replace: true });
        } catch (err) {
            alert(formatApiErrorDetail(err.response?.data?.detail) || "Verwijderen mislukt");
        }
    };

    if (loadError) {
        return (
            <div>
                <Link
                    to="/instellingen"
                    className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-wider text-ink-500 hover:text-ink-900 mb-6"
                    data-testid="back-to-settings"
                >
                    <ArrowLeft className="h-3.5 w-3.5" /> Terug naar instellingen
                </Link>
                <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
                    {loadError}
                </div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="p-10 text-sm text-ink-500" data-testid="tenant-detail-loading">
                Laden…
            </div>
        );
    }

    const t = data.tenant;
    const users = data.users || [];
    const counts = data.counts || {};

    return (
        <div className="p-5 sm:p-7 lg:p-10 xl:p-12 max-w-[1180px] mx-auto pb-24" data-testid="tenant-detail-page">
            <Link
                to="/instellingen"
                className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-wider text-ink-500 hover:text-ink-900 mb-6"
                data-testid="back-to-settings"
            >
                <ArrowLeft className="h-3.5 w-3.5" /> Terug naar instellingen
            </Link>
            <PageHeader
                kicker="Tenantdetail"
                title={t.name}
                subtitle={
                    t.system
                        ? "Systeem-tenant (Florefy intern)."
                        : "Beheer de gebruikers binnen deze tenant. Alle data blijft strikt geïsoleerd."
                }
                actions={
                    !t.system && (
                        <button
                            onClick={removeTenant}
                            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm border border-red-200 text-red-700 rounded-md hover:bg-red-50"
                            data-testid="delete-tenant-detail-btn"
                        >
                            <Trash2 className="h-4 w-4" /> Tenant verwijderen
                        </button>
                    )
                }
                testid="tenant-detail-header"
            />

            <section className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="tenant-counts">
                <Stat label="Gebruikers" value={counts.users} icon={Users} />
                <Stat label="Producten" value={counts.products} icon={Building2} />
                <Stat label="Klanten" value={counts.customers} icon={Building2} />
                <Stat label="Bestellingen" value={counts.orders} icon={Building2} />
            </section>

            <div className="flex items-center justify-between mb-4">
                <h2 className="flex items-center gap-2 text-lg font-semibold text-ink-900">
                    <Users className="h-4 w-4" /> Gebruikers in {t.name}
                </h2>
                {!t.system && (
                    <button
                        onClick={() => setShowForm((s) => !s)}
                        className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-florefy-900 text-white rounded-md hover:bg-florefy-800"
                        data-testid="add-tenant-user-btn"
                    >
                        <Plus className="h-4 w-4" /> Gebruiker aan deze tenant koppelen
                    </button>
                )}
            </div>

            {showForm && !t.system && (
                <form
                    onSubmit={save}
                    className="bg-white border border-border rounded-lg p-4 mb-4 grid grid-cols-1 md:grid-cols-2 gap-3"
                    data-testid="tenant-user-form"
                >
                    <input
                        type="text"
                        placeholder="Naam"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        required
                        className="px-3 py-2 rounded-md border border-border text-sm"
                        data-testid="tenant-user-name-input"
                    />
                    <input
                        type="email"
                        placeholder="E-mail"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        required
                        className="px-3 py-2 rounded-md border border-border text-sm"
                        data-testid="tenant-user-email-input"
                    />
                    <input
                        type="password"
                        placeholder="Wachtwoord (min. 8 tekens)"
                        value={form.password}
                        onChange={(e) => setForm({ ...form, password: e.target.value })}
                        required
                        minLength={8}
                        className="px-3 py-2 rounded-md border border-border text-sm"
                        data-testid="tenant-user-password-input"
                    />
                    <select
                        value={form.role}
                        onChange={(e) => setForm({ ...form, role: e.target.value })}
                        className="px-3 py-2 rounded-md border border-border text-sm bg-white"
                        data-testid="tenant-user-role-select"
                    >
                        <option value="member">Medewerker</option>
                        <option value="tenant_admin">Beheerder</option>
                    </select>
                    {error && (
                        <div className="md:col-span-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
                            {error}
                        </div>
                    )}
                    <div className="md:col-span-2 flex gap-2">
                        <button
                            type="submit"
                            disabled={saving}
                            className="px-3 py-1.5 text-sm bg-florefy-900 text-white rounded-md disabled:opacity-60"
                            data-testid="tenant-user-save-btn"
                        >
                            {saving ? "Opslaan…" : `Koppel aan ${t.name}`}
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                setShowForm(false);
                                setForm(emptyUser);
                                setError("");
                            }}
                            className="px-3 py-1.5 text-sm border border-border rounded-md"
                        >
                            Annuleren
                        </button>
                    </div>
                </form>
            )}

            <div className="bg-white border border-border rounded-lg overflow-hidden">
                <table className="w-full text-sm" data-testid="tenant-users-table">
                    <thead className="bg-ink-50 text-left text-xs uppercase font-mono tracking-wider text-ink-500">
                        <tr>
                            <th className="px-4 py-2">Naam</th>
                            <th className="px-4 py-2">E-mail</th>
                            <th className="px-4 py-2">Rol</th>
                            <th className="px-4 py-2">Tenant</th>
                            <th className="px-4 py-2 w-12"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map((u) => (
                            <tr
                                key={u.id}
                                className="border-t border-border"
                                data-testid={`tenant-user-row-${u.email}`}
                            >
                                <td className="px-4 py-2">{u.name}</td>
                                <td className="px-4 py-2 font-mono text-xs">{u.email}</td>
                                <td className="px-4 py-2">{ROLE_LABELS[u.role] || u.role}</td>
                                <td className="px-4 py-2 text-xs text-ink-500">{t.name}</td>
                                <td className="px-4 py-2">
                                    {u.role !== "super_admin" && (
                                        <div className="flex items-center justify-end gap-1">
                                            <button
                                                onClick={() =>
                                                    setResetting({ user: u, pwd: "", error: "", done: false })
                                                }
                                                className="text-ink-500 hover:text-florefy-900"
                                                title="Wachtwoord resetten"
                                                data-testid={`reset-password-${u.email}`}
                                            >
                                                <KeyRound className="h-4 w-4" />
                                            </button>
                                            <button
                                                onClick={() => removeUser(u)}
                                                className="text-ink-500 hover:text-red-600"
                                                title="Verwijderen"
                                                data-testid={`delete-tenant-user-${u.email}`}
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        </div>
                                    )}
                                </td>
                            </tr>
                        ))}
                        {users.length === 0 && (
                            <tr>
                                <td colSpan="5" className="px-4 py-6 text-center text-ink-500 text-sm">
                                    Nog geen gebruikers in deze tenant.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            {resetting && (
                <div
                    className="fixed inset-0 bg-florefy-900/40 flex items-center justify-center z-50 p-4"
                    onClick={() => setResetting(null)}
                    data-testid="reset-password-modal"
                >
                    <div
                        className="bg-white border border-border rounded-md w-full max-w-md p-6 mx-auto"
                        onClick={(e) => e.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="reset-password-title"
                    >
                        <h3 id="reset-password-title" className="text-lg font-semibold text-ink-900 flex items-center gap-2 mb-2">
                            <KeyRound className="h-5 w-5 text-florefy-900" /> Wachtwoord resetten
                        </h3>
                        <p className="text-sm text-ink-500 mb-4">
                            Stel een nieuw tijdelijk wachtwoord in voor{" "}
                            <span className="font-mono text-xs text-ink-900">{resetting.user.email}</span>.
                            Geef het wachtwoord persoonlijk door — de gebruiker kan het zelf wijzigen na inloggen.
                        </p>
                        {resetting.done ? (
                            <div className="bg-florefy-50 border border-florefy-200 text-florefy-900 text-sm px-3 py-3 rounded-sm mb-4" data-testid="reset-success">
                                ✓ Wachtwoord bijgewerkt. Geef het nieuwe wachtwoord aan de gebruiker.
                            </div>
                        ) : (
                            <>
                                <label className="block text-xs font-medium text-ink-700 mb-1">
                                    Nieuw wachtwoord (min. 8 tekens)
                                </label>
                                <input
                                    type="password"
                                    autoComplete="new-password"
                                    value={resetting.pwd}
                                    onChange={(e) =>
                                        setResetting({ ...resetting, pwd: e.target.value, error: "" })
                                    }
                                    className="w-full px-3 py-2 rounded-md border border-border font-mono text-sm focus:outline-none focus:ring-2 focus:ring-florefy-900"
                                    autoFocus
                                    data-testid="input-reset-password"
                                />
                                {resetting.error && (
                                    <div className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 px-2 py-1.5 rounded-sm">
                                        {resetting.error}
                                    </div>
                                )}
                            </>
                        )}
                        <div className="flex justify-end gap-2 mt-5">
                            <button
                                onClick={() => setResetting(null)}
                                className="px-3 py-1.5 text-sm border border-border rounded-md"
                                data-testid="btn-close-reset"
                            >
                                {resetting.done ? "Sluiten" : "Annuleren"}
                            </button>
                            {!resetting.done && (
                                <button
                                    onClick={submitReset}
                                    className="px-3 py-1.5 text-sm bg-florefy-900 text-white rounded-md hover:bg-florefy-800"
                                    data-testid="btn-confirm-reset"
                                >
                                    Wachtwoord resetten
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function Stat({ label, value, icon: Icon }) {
    return (
        <div className="bg-white border border-border rounded-lg p-4">
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-ink-500">
                <Icon className="h-3 w-3" />
                {label}
            </div>
            <div className="text-2xl font-semibold text-ink-900 mt-1">{value ?? 0}</div>
        </div>
    );
}
